import Utils from './';
import AppTypeUtils from './appType';

export enum Os {
	IOs = 'iOs',
	Android = 'android',
	Blackberry = 'blackberry',
	Firefox = 'firefox',
	Tizen = 'tizen',
	Windows = 'windows',
	Mac = 'mac',
	Linux = 'linux'
}

export enum Browser {
	Safari = 'safari',
	Chrome = 'chrome',
	Firefox = 'firefox',
	Android = 'android',
	Tizen = 'tizen',
	Blackberry = 'blackberry',
	IE = 'ie',
	Edge = 'edge'
}

export enum Format {
	Phablet = 'phablet',
	Tablet = 'tab',
	Phone = 'phone',
	Desktop = 'desktop'
}

const Unknown = 'unknown';

const toFindArray = [
	'android',
	'blackberry',
	'chrome',
	'edge',
	'firefox',
	'ipad',
	'iphone',
	'linux',
	'mac os',
	'mobile',
	'phone',
	'safari',
	'tablet',
	'tizen',
	'touch',
	'trident',
	'windows'
];

interface ConnectionObserver {
	toCall: () => void;
	isOnce: boolean;
}

class UserAgentUtils {

	public static isMobileApp: boolean;
	public static checkOnlineStatusDomain: string = 'https://app.uprez.fr/online';
	private static userAgent: string = navigator.userAgent.toLowerCase();
	private static os = { name: Unknown, version: Unknown };
	private static device = { type: Unknown };
	private static browser = { name: Unknown, version: Unknown };
	private static isOnlineValue: boolean;
	private static connectionIsUpObserver: Array<ConnectionObserver> = [];
	private static connectionIsDownObserver: Array<ConnectionObserver> = [];

	public static isDesktop = (): boolean => UserAgentUtils.device.type === Format.Desktop;
	public static isPhone = (): boolean => UserAgentUtils.device.type === Format.Phone;
	public static isTablet = (): boolean => UserAgentUtils.device.type === Format.Tablet || UserAgentUtils.device.type === Format.Phablet;
	public static isPhablet = (): boolean => UserAgentUtils.device.type === Format.Phablet;
	public static isUnknown = (): boolean => UserAgentUtils.device.type === Unknown;
	public static isIE = (): boolean => UserAgentUtils.browser.name === Browser.IE;
	public static isSafari = (): boolean => UserAgentUtils.browser.name === Browser.Safari;
	public static isChrome = (): boolean => UserAgentUtils.browser.name === Browser.Chrome;
	public static isWindows = (): boolean => UserAgentUtils.os.name === Os.Windows;
	public static isAndroid = (): boolean => UserAgentUtils.os.name === Os.Android;
	public static isIOS = (): boolean => UserAgentUtils.os.name === Os.IOs;
	public static isMac = (): boolean => UserAgentUtils.os.name === Os.Mac;
	public static isWindowsApp = (): boolean => UserAgentUtils.isWindows() && UserAgentUtils.isMobileApp;
	public static isAndroidApp = (): boolean => UserAgentUtils.isAndroid() && UserAgentUtils.isMobileApp;
	public static isIOSApp = (): boolean => UserAgentUtils.isIOS() && UserAgentUtils.isMobileApp;
	public static isMacApp = (): boolean => UserAgentUtils.isMac() && UserAgentUtils.isMobileApp;
	public static isOnline = (): boolean => {
		return UserAgentUtils.isMobileApp ? UserAgentUtils.isOnlineValue : true;
	}
	public static isOnlinePromise = (): Promise<boolean> => {
		return UserAgentUtils.updateOnlineStatus();
	}
	public static isTouchSupported = (): boolean => {
		return 'ontouchstart' in window ||
			navigator.maxTouchPoints > 0 ||
			navigator.msMaxTouchPoints > 0;
	}

	public static subscribeToConnectionIsUp = (toCall: () => void, isOnce?: boolean): void => {
		UserAgentUtils.subscribeConnectionObserver(
			UserAgentUtils.connectionIsUpObserver,
			toCall,
			isOnce,
		);
	}

	public static subscribeToConnectionIsDown = (toCall: () => void, isOnce?: boolean): void => {
		UserAgentUtils.subscribeConnectionObserver(
			UserAgentUtils.connectionIsDownObserver,
			toCall,
			isOnce,
		);
	}

	public static initApp() {
		UserAgentUtils.isMobileApp = AppTypeUtils.isMobileAppMainView;
		if (UserAgentUtils.isMobileApp) {
			setInterval(() => { UserAgentUtils.updateOnlineStatus(); }, 3000);
		}
	}

	public static init() {
		UserAgentUtils.constructObject();
		UserAgentUtils.os.version = UserAgentUtils.osVersion;
		UserAgentUtils.browser.version = UserAgentUtils.browserVersion;
	}

	private static subscribeConnectionObserver = (connectionObservers: Array<ConnectionObserver>, toCall: () => void, isOnce: boolean = false): void => {
		connectionObservers.push({
			toCall,
			isOnce,
		});
	}

	private static getCheckOnlineStatusUrl = (): string => `${UserAgentUtils.checkOnlineStatusDomain}/?rand=${Math.random()}`;

	private static updateOnlineStatus = (): Promise<boolean> => {
		let oldIsOnlineValue: boolean;
		let newIsOnlineValue: boolean;

		return UserAgentUtils.checkOnlineStatus().then(isOnline => {
			oldIsOnlineValue = UserAgentUtils.isOnlineValue;
			newIsOnlineValue = isOnline;

			UserAgentUtils.isOnlineValue = newIsOnlineValue;

			if (oldIsOnlineValue === false && newIsOnlineValue) {
				UserAgentUtils.notifyConnectionIsUpObserver();
			}

			if (oldIsOnlineValue && !newIsOnlineValue) {
				UserAgentUtils.notifyConnectionIsDownObserver();
			}

			return UserAgentUtils.isOnlineValue;
		});
	}

	private static checkOnlineStatus = () =>
		new Promise<boolean>((resolve): Promise<boolean> => {
			// Note: withCredentials is explicitly set to false here because this is a public endpoint (app.uprez.fr/online)
			// that doesn't require authentication and may be on a different domain.
			// All other API calls should use the default withCredentials=true (which is the default parameter value).
			// Parameters: url, protocol, isAsync, ignoreConnectionStatus, dataInUrl, dataInBody, contentType, withCredentials, preventRedirect, preventSecurityHeader
			return Utils.fetchText(UserAgentUtils.getCheckOnlineStatusUrl(), 'GET', true, true, null, undefined, undefined, false, true, true)
				.then((result) => {
					resolve(true);
					return true;
				})
				.catch((e) => {
					resolve(false);
					return false;
				})
			;
		})

	private static notifyConnectionIsUpObserver = (): void => {
		UserAgentUtils.notifyObserver(UserAgentUtils.connectionIsUpObserver);
	}

	private static notifyConnectionIsDownObserver = (): void => {
		UserAgentUtils.notifyObserver(UserAgentUtils.connectionIsDownObserver);
	}

	private static notifyObserver = (connectionObservers: Array<ConnectionObserver>): void => {
		for (let i = connectionObservers.length - 1; i >= 0; i--) {
			const connectionObserver = connectionObservers[i];
			connectionObserver.toCall();
			if (connectionObserver.isOnce) {
				connectionObservers.splice(i, 1);
			}
		}
	}

	private static resolveWindowsVersion = (): string => {
		const version = (/Windows Phone ([0-9.]+)/i.exec(navigator.userAgent) || ['', Unknown])[1];

		if (version !== Unknown) {
			return version;
		}

		const nt = (/Windows NT ([0-9.]+)/i.exec(navigator.userAgent) || ['', Unknown])[1];
		switch (nt) {
			case '10.0':
				return '10';
			case '6.3':
				return '8.1';
			case '6.2':
				return '8';
			case '6.1':
				return '7';
			case '6.0':
				return 'Vista';
			case '5.2':
			case '5.1':
				return 'XP';
			case '5.0':
				return '2000';
			default:
				return Unknown;
		}
	}

	private static resolveMacVersion = (): string => {
		const version = /Mac Os.* ([0-9_]+)/i.exec(navigator.userAgent);
		if (version && version[1]) {
			return version[1].split('_').slice(0, 2).join('.');
		} else {
			return Unknown;
		}
	}

	private static get osVersion(): string {
		switch (UserAgentUtils.os.name) {
			case Os.IOs:
				return (/CPU.*Os ([0-9]{1,5})/i.exec(navigator.userAgent) || ['', Unknown])[1];
			case Os.Android:
				return (/Android ([0-9.]+)/i.exec(navigator.userAgent) || ['', Unknown])[1];
			case Os.Windows:
				return UserAgentUtils.resolveWindowsVersion();
			case Os.Mac:
				return UserAgentUtils.resolveMacVersion();
			default:
				return Unknown;
		}
	}


	private static resolveIeVersion = (): string => {
		let version = (/rv\:([0-9]+)/.exec(navigator.userAgent) || ['', Unknown])[1];
		if (version === Unknown) {
			version = (/IEMobile\/([0-9]+)/.exec(navigator.userAgent) || ['', Unknown])[1];
		}
		if (version === Unknown) {
			version = (/MSIE\s([0-9]+)/.exec(navigator.userAgent) || ['', Unknown])[1];
		}
		return version;
	}

	private static get browserVersion(): string {
		switch (UserAgentUtils.browser.name) {
			case Browser.Chrome:
				return (/Chrome\/([0-9]+)/.exec(navigator.userAgent) || ['', Unknown])[1];
			case Browser.Firefox:
				return (/Firefox\/([0-9]+)/.exec(navigator.userAgent) || ['', Unknown])[1];
			case Browser.IE:
				return UserAgentUtils.resolveIeVersion();
			case Browser.Edge:
				return (/Edge\/([0-9]+)\./.exec(navigator.userAgent) || ['', Unknown])[1];
			case Browser.Safari:
				return (/Version\/([0-9]+\.[0-9]+)/.exec(navigator.userAgent) || ['', Unknown])[1];
			default:
				return Unknown;
		}
	}

	private static constructObject = (): void => {
		const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
		const screenHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
		const width = Math.max(screenWidth, screenHeight);

		let toFindPos: { [k: string]: boolean } = {};
		toFindArray.forEach((element) => {
			toFindPos[element] = UserAgentUtils.userAgent.includes(element);
		});

		// iOs
		// IE cheat and says it's iphone
		if ((toFindPos.iphone || toFindPos.ipad) && !toFindPos.trident) {
			UserAgentUtils.os.name = Os.IOs;
			UserAgentUtils.device.type = Format.Tablet;
			UserAgentUtils.browser.name = Browser.Safari;

			if (toFindPos.iphone) {
				UserAgentUtils.device.type = Format.Phone;

				if (width > 750 && !toFindPos.safari) {
					UserAgentUtils.device.type = Format.Tablet;
				}
			} else if (width < 750) {
				UserAgentUtils.device.type = Format.Phablet;
			}

			return;
		}

		// mac Os
		// IE cheat and says it's mac os
		if (toFindPos['mac os'] && !toFindPos.trident) {
			UserAgentUtils.os.name = Os.Mac;
			UserAgentUtils.device.type = Format.Desktop;

			// Since IOS13, user agent returns Mac OS instead of IOS for IOS devices by default
			// A hack to separate Devices from Desktop is to check if the navigator uses Touch.
			const isNotDesktop = navigator.maxTouchPoints > 0;

			if (isNotDesktop) {
				UserAgentUtils.os.name = Os.IOs;
				UserAgentUtils.device.type = Format.Phone;

				if (width > 750) {
					UserAgentUtils.device.type = Format.Tablet;
				}
			}

			if (toFindPos.chrome) {
				UserAgentUtils.browser.name = Browser.Chrome;
			} else if (toFindPos.firefox) {
				UserAgentUtils.browser.name = Browser.Firefox;
			} else {
				UserAgentUtils.browser.name = Browser.Safari;
			}
			return;
		}

		// Windows
		if (toFindPos.windows) {
			UserAgentUtils.os.name = Os.Windows;

			if (toFindPos.chrome) {
				UserAgentUtils.browser.name = Browser.Chrome;
			} else if (toFindPos.firefox) {
				UserAgentUtils.browser.name = Browser.Firefox;
			} else if (toFindPos.edge) {
				UserAgentUtils.os.name = Os.Windows;
				UserAgentUtils.browser.name = Browser.Edge;
			} else {
				UserAgentUtils.browser.name = Browser.IE;
			}

			if (toFindPos.mobile || toFindPos.phone) {
				UserAgentUtils.device.type = Format.Phone;
			} else {
				UserAgentUtils.device.type = Format.Desktop;
			}

		}

		// linux
		if (toFindPos.linux) {
			UserAgentUtils.os.name = Os.Linux;

			if (toFindPos.chrome) {
				UserAgentUtils.browser.name = Browser.Chrome;
			} else if (toFindPos.firefox) {
				UserAgentUtils.browser.name = Browser.Firefox;
			}

			UserAgentUtils.device.type = Format.Desktop;
		}

		// Tizen
		if (toFindPos.tizen) {
			UserAgentUtils.os.name = Os.Tizen;
			UserAgentUtils.browser.name = Browser.Tizen;
			return;
		}

		// Blackberry
		if (toFindPos.blackberry) {
			UserAgentUtils.os.name = Os.Blackberry;
			UserAgentUtils.browser.name = Browser.Blackberry;
			return;
		}

		// Android
		// IE cheat and says it's android
		if (toFindPos.android && !toFindPos.trident) {
			UserAgentUtils.os.name = Os.Android;
			UserAgentUtils.device.type = Format.Tablet;

			if (toFindPos.chrome) {
				UserAgentUtils.browser.name = Browser.Chrome;
			} else if (toFindPos.firefox) {
				UserAgentUtils.browser.name = Browser.Firefox;
			} else {
				UserAgentUtils.browser.name = Browser.Android;
			}
		} else if (toFindPos.firefox && (toFindPos.mobile || toFindPos.tablet)) {
			// Firefox
			UserAgentUtils.os.name = Os.Firefox;
			UserAgentUtils.browser.name = Browser.Firefox;
		}

		// Check format
		if (toFindPos.mobile) {
			UserAgentUtils.device.type = Format.Phone;
		} else if (UserAgentUtils.device.type !== Format.Desktop) {
			if (width / window.devicePixelRatio < 750) {
				UserAgentUtils.device.type = Format.Phablet;
			} else if (width / window.devicePixelRatio <= 1280) {
				UserAgentUtils.device.type = Format.Tablet;
			} else {
				UserAgentUtils.device.type = Format.Desktop;
			}
		}
	}
}

export default UserAgentUtils;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/userAgent.ts